import { useSyncExternalStore } from 'react';
import { authHeaders } from '@/lib/authHeaders';

/**
 * Per-user profile photo — cached locally for instant paint AND stored on the
 * server per account, so once uploaded it stays forever on every device.
 */
const key = (email: string) => `lexidy-avatar-${email.toLowerCase()}`;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Pull the account's stored photo at app start (server is the source of truth). */
export async function hydrateAvatarFromServer(email: string): Promise<void> {
  if (!email) return;
  try {
    const r = await fetch('/api/account/avatar', { headers: await authHeaders() });
    if (!r.ok) return;
    const { data } = (await r.json()) as { data: string | null };
    try {
      if (data) localStorage.setItem(key(email), data);
      // No server photo but one cached locally → keep local and push it up
      // (covers photos uploaded before server storage existed).
      else {
        const local = localStorage.getItem(key(email));
        if (local) { void pushAvatar(local); return; }
      }
    } catch { /* ignore */ }
    emit();
  } catch { /* offline — local cache still shows */ }
}

async function pushAvatar(dataUrl: string | null) {
  try {
    await fetch('/api/account/avatar', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ data: dataUrl }),
    });
  } catch { /* best-effort */ }
}

export function setAvatar(email: string, dataUrl: string | null) {
  if (!email) return;
  try {
    if (dataUrl) localStorage.setItem(key(email), dataUrl);
    else localStorage.removeItem(key(email));
  } catch {
    /* storage full / unavailable — ignore */
  }
  emit();
  void pushAvatar(dataUrl);
}

export function useAvatar(email: string | undefined): string | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => (email ? localStorage.getItem(key(email)) : null),
    () => null,
  );
}
